// resources/js/services/NotificationService.js
//
// ============================================================================
// THIS FILE IS DEAD. NOTHING IN IT RUNS. Do not add features to it, and do not
// "fix" anything here: the fix will not reach a user.
//
// `this.initialized` is assigned in exactly one place, init(), and init()'s only
// caller is commented out at notification-init.js:21. Every method behind that
// flag is unreachable.
//
// THE LIVE BROWSER-NOTIFICATION PATH IS:
//   1. app/Jobs/SendWhatsappMessagePushNotification.php  builds title and body
//   2. public/service-worker.js  `push` handler shows it
//   3. resources/js/services/PushNotificationService.js  subscribes the browser
//
// BRO-1567 cost a full commit to this. Privacy masking was added HERE, with five
// tests, two of them mutated and watched to fail, and a commit message asserting
// that browser notifications carried no name or number. They still did. Mutation
// testing cannot catch it: mutating dead code produces a dead mutant that the
// dead tests still catch, so the signal is identical to that of live code.
//
// The privacy fix now lives where the notification is actually built: the service
// worker asks any open window whether privacy mode is on and replaces the content.
// See resources/js/utils/privacyServiceWorkerBridge.ts.
// ============================================================================

import PushNotificationService from './PushNotificationService';
import { clientDisplayName } from '@/utils/clientDisplay';
import { getPrivacySnapshot } from '@/utils/privacyMode';

// Initialize the message tracking system at the global level
if (typeof window !== 'undefined' && !window._notifiedMessageIds) {
  window._notifiedMessageIds = new Set();
  console.debug('Initialized global _notifiedMessageIds Set from NotificationService');
}

class NotificationService {
    constructor() {
        this.initialized = false;
        this.enabled = false;
        this.permission = null;
        this.companyId = null;
        this.userId = null;
        this.pushService = PushNotificationService;
        this.notificationPreferences = {
            // Snake case keys from the API
            browser_notifications_enabled: true,
            message_notifications: true,
            contact_notifications: true,
            sound_enabled: true,
            // Camel case keys for backward compatibility
            browserNotificationsEnabled: true,
            messageNotifications: true,
            contactNotifications: true,
            soundEnabled: true
        };

        // Keep track of all shown notifications to prevent duplicates
        this.shownNotifications = new Set();

        // Define custom event names
        this.events = {
            READY: 'broadcaster-notifications-ready',
            STATUS_CHANGED: 'broadcaster-notifications-status-changed',
            MESSAGE_RECEIVED: 'broadcaster-message-received',
            NOTIFICATION_SHOWN: 'broadcaster-notification-shown'
        };
    }

    /**
     * Initialize the notification service
     * @param {Object} options Configuration options
     */
    init(options = {}) {
        if (this.initialized) return;

        // Set up options
        this.companyId = options.companyId || window.BROADCASTER_COMPANY_ID || null;
        this.userId = options.userId || null;

        // Override default notification preferences with user preferences if available
        if (options.notificationPreferences) {
            this.notificationPreferences = {
                ...this.notificationPreferences,
                ...options.notificationPreferences
            };
        }
        
        // Try to get user preferences from global variable
        if (window.__BROADCASTER_USER_PREFERENCES) {
            console.debug('NotificationService: Using global user preferences:', window.__BROADCASTER_USER_PREFERENCES);
            this.notificationPreferences = {
                ...this.notificationPreferences,
                ...window.__BROADCASTER_USER_PREFERENCES
            };
        } else {
            // If not available, fetch from API
            this._fetchUserPreferences();
        }

        // Check if notifications are supported
        if (!this.isSupported()) {
            console.warn('Browser notifications are not supported in this browser');
            return;
        }

        // Check notification permission
        this.permission = Notification.permission;
        this.enabled = this.permission === 'granted';

        // Setup event listeners
        this._setupEventListeners();

        // Auto request permission if set in options
        if (options.autoRequestPermission && this.permission === 'default') {
            this.requestPermission();
        }

        this.initialized = true;

        // Dispatch ready event
        this._dispatchEvent(this.events.READY, {
            enabled: this.enabled,
            permission: this.permission,
            preferences: this.notificationPreferences
        });

        console.debug('🔔 NotificationService initialized:', {
            enabled: this.enabled,
            permission: this.permission,
            companyId: this.companyId,
            preferences: this.notificationPreferences
        });
    }

    /**
     * Check if browser notifications are supported
     */
    isSupported() {
        return 'Notification' in window;
    }

    /**
     * Get current notification status
     */
    getStatus() {
        return {
            initialized: this.initialized,
            enabled: this.enabled,
            permission: this.permission,
            supported: this.isSupported(),
            preferences: this.notificationPreferences
        };
    }

    /**
     * Request notification permission
     * @returns {Promise} Promise that resolves with the permission result
     */
    async requestPermission() {
        if (!this.isSupported()) {
            return Promise.reject('Notifications not supported');
        }

        try {
            const permission = await Notification.requestPermission();
            this.permission = permission;
            this.enabled = permission === 'granted';

            // Dispatch status changed event
            this._dispatchEvent(this.events.STATUS_CHANGED, {
                enabled: this.enabled,
                permission: this.permission
            });

            // If permission was granted, save the preference to the server
            if (permission === 'granted') {
                this._saveNotificationPreference('browser_notifications_enabled', true);
            }

            return permission;
        } catch (error) {
            console.error('Error requesting notification permission:', error);
            return 'denied';
        }
    }

    /**
     * Enable or disable notifications
     * @param {boolean} enabled Whether notifications should be enabled
     */
    setEnabled(enabled) {
        // Can't enable if permission is not granted
        if (enabled && this.permission !== 'granted') {
            this.requestPermission();
            return;
        }

        this.enabled = enabled;

        // Save preference to the server
        this._saveNotificationPreference('browser_notifications_enabled', enabled);

        // Dispatch status changed event
        this._dispatchEvent(this.events.STATUS_CHANGED, {
            enabled: this.enabled,
            permission: this.permission
        });
    }

    /**
     * Update notification preferences
     * @param {Object} preferences New notification preferences
     */
    updatePreferences(preferences) {
        // Update preferences with both snake_case and camelCase keys
        const updatedPreferences = { ...this.notificationPreferences };
        
        // Process each preference and update both formats
        Object.entries(preferences).forEach(([key, value]) => {
            // Update the provided key
            updatedPreferences[key] = value;
            
            // Also update the corresponding camelCase/snake_case key
            if (key === 'browser_notifications_enabled') {
                updatedPreferences.browserNotificationsEnabled = value;
            } else if (key === 'browserNotificationsEnabled') {
                updatedPreferences.browser_notifications_enabled = value;
            } else if (key === 'message_notifications') {
                updatedPreferences.messageNotifications = value;
            } else if (key === 'messageNotifications') {
                updatedPreferences.message_notifications = value;
            } else if (key === 'contact_notifications') {
                updatedPreferences.contactNotifications = value;
            } else if (key === 'contactNotifications') {
                updatedPreferences.contact_notifications = value;
            } else if (key === 'sound_enabled') {
                updatedPreferences.soundEnabled = value;
            } else if (key === 'soundEnabled') {
                updatedPreferences.sound_enabled = value;
            }
        });
        
        // Update the preferences
        this.notificationPreferences = updatedPreferences;

        // Save preferences to the server
        Object.entries(preferences).forEach(([key, value]) => {
            this._saveNotificationPreference(key, value);
        });

        // Dispatch status changed event
        this._dispatchEvent(this.events.STATUS_CHANGED, {
            enabled: this.enabled,
            permission: this.permission,
            preferences: this.notificationPreferences
        });
    }

    /**
     * Detect if the current device is mobile
     * @private
     */
    _isMobile() {
        const userAgent = navigator.userAgent || navigator.vendor || window.opera;
        return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile|tablet/i.test(userAgent.toLowerCase());
    }

    /**
     * Show a notification
     * @param {Object} options Notification options
     */
    /**
     * BRO-1536: THIS METHOD IS UNREACHABLE, and that is why the DND check is gone from it.
     *
     * `this.initialized` is assigned in exactly one place, init(), and init() has no
     * caller: notification-init.js:25-28 comments out the only one. So this returns on
     * its first line every time, and handleMessageEvent()/handleContactEvent() below,
     * which are its only callers, have no callers of their own.
     *
     * A client-side reimplementation of the Do Not Disturb decision used to live here,
     * roughly 60 lines mirroring DndService: business hours, custom schedules,
     * wrap-around, company timezone. It was read as a live second implementation twice,
     * by two people, and produced a wrong premise for BRO-1536 both times. It never
     * decided anything. Deleted rather than left dormant, because code that is correct,
     * well-commented and never called is more misleading than code that is wrong.
     *
     * Browser and phone notifications are ONE path: web push, rendered by
     * public/service-worker.js, and gated server-side before the push is sent by
     * SendWhatsappMessagePushNotification.php -> DndService::isCurrentlyDnd(). The server
     * decides; there is no client decision to make.
     *
     * Left in place rather than removed wholesale because the surrounding class is still
     * constructed for its notificationPreferences and permission fields. See BRO-1544 for
     * the general question of finding code in this state.
     */
    async showNotification(options) {
        if (!this.initialized || !this.enabled || this.permission !== 'granted') {
            console.warn('Notifications are not enabled or initialized');
            return null;
        }

        // Check if notification preferences allow this notification
        if (!this._checkNotificationPreferences(options.type)) {
            console.debug(`Notification of type ${options.type} is disabled in preferences`);
            return null;
        }

        // Create a unique ID for this notification to prevent duplicates
        const notificationId = options.id || `${options.type}-${Date.now()}`;

        // Check if this notification has already been shown recently
        if (this.shownNotifications.has(notificationId)) {
            console.debug(`Notification ${notificationId} already shown recently, skipping`);
            return null;
        }

        // Detect platform
        const isMobile = this._isMobile();
        
        // Default options
        const defaultOptions = {
            icon: '/android-chrome-192x192.png',
            badge: '/favicon-32x32.png',
            // Mobile: persist in tray, Desktop: auto-dismiss
            requireInteraction: options.requireInteraction !== undefined 
                ? options.requireInteraction 
                : isMobile,
            // Set silent based on sound preferences
            silent: !(this.notificationPreferences.soundEnabled || this.notificationPreferences.sound_enabled)
        };
        
        console.debug(`Notification settings - Platform: ${isMobile ? 'mobile' : 'desktop'}, Sound enabled: ${this.notificationPreferences.soundEnabled || this.notificationPreferences.sound_enabled}`);

        try {
            // Check if service worker is available and use it for notifications
            if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
                // Use service worker to show notification for consistency
                const registration = await navigator.serviceWorker.ready;
                
                // Prepare notification data for service worker
                const notificationData = {
                    ...defaultOptions,
                    ...options,
                    body: options.body || '',
                    tag: notificationId,
                    data: {
                        url: options.url || '/',
                        type: options.type,
                        client_id: options.data?.message?.client_id || options.data?.id,
                        platform: isMobile ? 'mobile' : 'desktop', // Pass platform info
                        ...options.data
                    },
                    isMobile: isMobile // Pass mobile flag directly for service worker
                };

                // Show notification via service worker
                await registration.showNotification(options.title || 'Broadcaster', notificationData);
                
                // Add to shown notifications set to prevent duplicates
                this.shownNotifications.add(notificationId);

                // Remove from shown notifications after 30 seconds to allow showing again
                setTimeout(() => {
                    this.shownNotifications.delete(notificationId);
                }, 30000);
                
                // Play sound if enabled in preferences
                if (this.notificationPreferences.soundEnabled || this.notificationPreferences.sound_enabled) {
                    this._playNotificationSound();
                }

                // Dispatch notification shown event
                this._dispatchEvent(this.events.NOTIFICATION_SHOWN, {
                    id: notificationId,
                    type: options.type,
                    title: options.title
                });

                return true;
            } else {
                // Fallback to direct notification API if service worker not available
                // This should rarely happen in modern browsers
                const notification = new Notification(options.title, {
                    ...defaultOptions,
                    ...options,
                    tag: notificationId,
                    dir: options.dir || 'ltr',
                    data: options.data || {}
                });

                // Add to shown notifications set to prevent duplicates
                this.shownNotifications.add(notificationId);

                // Remove from shown notifications after 30 seconds to allow showing again
                setTimeout(() => {
                    this.shownNotifications.delete(notificationId);
                }, 30000);

                // Play sound if enabled in preferences
                if (this.notificationPreferences.soundEnabled || this.notificationPreferences.sound_enabled) {
                    this._playNotificationSound();
                }

                // Add click handler for fallback notification
                notification.onclick = () => {
                    window.focus();

                    // Execute custom click handler if provided
                    if (typeof options.onClick === 'function') {
                        options.onClick();
                    }

                    // Navigate to URL if provided
                    if (options.url) {
                        window.location.href = options.url;
                    }

                    // Close the notification
                    notification.close();
                };

                // Dispatch notification shown event
                this._dispatchEvent(this.events.NOTIFICATION_SHOWN, {
                    id: notificationId,
                    type: options.type,
                    title: options.title
                });

                return notification;
            }
        } catch (error) {
            console.error('Error showing notification:', error);
            return null;
        }
    }

    /**
     * Handle a message event from WebSocket/Echo
     * @param {Object} message The message object
     */
    handleMessageEvent(message) {
        // Add detailed debug logging to see exactly what comes from Reverb
        console.debug('====== DEBUG: NOTIFICATION SERVICE MESSAGE ======');
        console.debug('Message full object:', JSON.stringify(message));
        console.debug('Message ID:', message?.id);
        console.debug('Message content:', message?.content);
        console.debug('Message contact_id:', message?.client_id);
        console.debug('Message contact_name:', message?.client_name);
        console.debug('Message direction:', message?.direction);
        console.debug('Message type:', message?.type);
        console.debug('All message keys:', Object.keys(message || {}));
        console.debug('================================================');
        
        // Skip if notifications not enabled
        if (!this.enabled || this.permission !== 'granted') {
            console.debug('NotificationService: Notifications not enabled or permission not granted');
            return;
        }

        // Check both the legacy flag mechanism and message ID tracking
        if (window._messageNotificationsDisabled) {
            console.debug('NotificationService: Message notifications temporarily disabled by NotificationBell, skipping:', message.id);
            return;
        }
        
        // Use message ID tracking for more precise duplicate prevention
        if (!window._notifiedMessageIds) {
            window._notifiedMessageIds = new Set();
        }
        
        // Check if we've already shown a notification for this message
        if (window._notifiedMessageIds.has(message.id)) {
            console.debug('NotificationService: Already notified about message ID:', message.id);
            return;
        }
        
        // Mark this message as notified
        window._notifiedMessageIds.add(message.id);
        console.debug(`NOTIFICATION TRACKING: NotificationService added message ID ${message.id} to tracking at ${new Date().toISOString()}`);
        
        // Log notification attempt
        console.debug('NotificationService: Attempting to show notification for message:', message.id);

        // Check if this is an inbound message
        if (message.direction !== 'inbound') {
            console.debug('Not an inbound message, skipping notification:', message.id);
            return;
        }

        // BRO-782: Use clientDisplayName for consistent display with username fallback
        let contactName = '';
        let phoneNumber = '';

        // Access client data if available
        if (message.client && typeof message.client === 'object') {
            contactName = clientDisplayName(message.client);
            phoneNumber = message.client.phone || message.from_number || '';
        } else {
            // Fallback to simpler properties
            contactName = this._resolveContactName(message);
            phoneNumber = message.sender_phone || message.client_phone || message.from_number || message.phone || '';
        }
        
        // Format phone number if available
        let formattedPhone = '';
        if (phoneNumber) {
            // Simple phone formatting - this could be enhanced for different country codes
            if (phoneNumber.startsWith('44')) {
                // UK number
                formattedPhone = `+${phoneNumber.slice(0, 2)} ${phoneNumber.slice(2, 5)} ${phoneNumber.slice(5)}`;
            } else {
                // Generic formatting
                formattedPhone = phoneNumber.replace(/^(\d{2})(\d{3})(\d+)$/, '+$1 $2 $3');
            }
        }
        
        // Format the message content
        const messageContent = message.content || message.message || 'New message received';
        const formattedMessage = messageContent.length > 60
            ? messageContent.substring(0, 57) + '...'
            : messageContent;

        // Format received time
        let receivedTime = '';
        if (message.received_at) {
            // Convert to local browser time
            const date = new Date(message.received_at);
            receivedTime = date.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
        } else {
            // Fallback to current time
            receivedTime = new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
        }

        /**
         * BRO-1567: with privacy mode on, a notification carries NO name, NO number and NO
         * message content.
         *
         * A notification is the one thing on this list that renders OUTSIDE the page, over
         * whatever is on screen, and arrives unbidden. Every other surface waits to be
         * looked at; this one interrupts. During the screen share this feature exists for,
         * an inbound message from a real customer would put their name and the first 57
         * characters of what they said on top of the demo.
         *
         * GATED ON PRIVACY MODE ITSELF, NOT ON isClientHidden, and that is deliberate.
         * Revealing a contact is a decision about a row you deliberately opened. A
         * notification is usually about a DIFFERENT contact, arrives without being asked
         * for, and is rendered by the operating system where nothing can be un-revealed
         * afterwards. So reveal does not re-enable it.
         *
         * The notification still fires, still says a message arrived, and still opens the
         * right conversation when clicked. Only the identifying content goes.
         */
        const privacyOn = getPrivacySnapshot().enabled;

        const title = privacyOn ? 'Broadcaster' : `Broadcaster Message from ${contactName}`;

        let notificationBody;

        if (privacyOn) {
            notificationBody = 'New message received';
            notificationBody += `\nReceived: ${receivedTime}`;
            notificationBody += '\nClick to view in Messages';
        } else {
            // Build a rich notification body - use minimal spacing for Firefox
            notificationBody = formattedMessage;

            // Add time info (moved from title to body)
            notificationBody += `\nReceived: ${receivedTime}`;

            // Add contact phone if available - use single line breaks for Firefox
            if (formattedPhone) {
                notificationBody += `\nPhone: ${formattedPhone}`;
            }

            // Add action hint - use single line break
            notificationBody += '\nClick to view in Messages';
        }

        // Show the notification with enhanced info and styling for Firefox
        this.showNotification({
            id: `message-${message.id}`,
            type: 'message',
            title: title,
            body: notificationBody,
            url: `/chat/${message.client_id}`,
            data: {
                message: message,
                style: 'text-align: left; white-space: pre-line;' // Custom styling
            },
            // requireInteraction will be set based on platform detection
            tag: `msg-${message.id}`, // Add tag for uniqueness
            dir: 'ltr' // Force left-to-right text direction
        });

        // Dispatch message received event
        this._dispatchEvent(this.events.MESSAGE_RECEIVED, message);
    }

    /**
     * Handle a contact event from WebSocket/Echo
     * @param {Object} contact The contact object
     */
    handleContactEvent(contact) {
        // Skip if notifications not enabled
        if (!this.enabled || this.permission !== 'granted') return;

        // Check if the page is focused
        if (document.hasFocus()) {
            console.debug('Page is focused, skipping notification for contact:', contact.id);
            return;
        }
        
        // Check if notifications are temporarily disabled by NotificationBell
        if (window._contactNotificationsDisabled) {
            console.debug('NotificationService: Contact notifications temporarily disabled by NotificationBell, skipping:', contact.id);
            return;
        }

        // Get contact name with fallback
        const contactName = contact.name || contact.phone || 'A new contact';

        // Show the notification
        this.showNotification({
            id: `new-contact-${contact.id}`,
            type: 'contact',
            title: 'New WhatsApp Contact',
            // BRO-1567: same rule as the message notification above - no name and no number
            // while privacy mode is on. A new contact arriving mid-demo is exactly the
            // moment somebody's details would appear over the top of the screen share.
            body: getPrivacySnapshot().enabled
                ? 'A new contact has been added\nClick to view in Messages'
                : `${contactName} has been added to your contacts\nClick to view in Messages`,
            url: `/chat/${contact.id}`,
            data: contact
            // requireInteraction will be set based on platform detection
        });
    }

    /**
     * Play notification sound
     * @private
     */
    _playNotificationSound() {
        try {
            const audio = new Audio('/sounds/notification.mp3');
            audio.volume = 0.5;
            
            // Add event handlers for debugging
            audio.onloadeddata = () => console.debug('🔔 Notification sound loaded successfully');
            audio.onplay = () => console.debug('🔔 Notification sound started playing');
            audio.onended = () => console.debug('🔔 Notification sound finished playing');
            audio.onerror = (e) => {
                console.error('🔔 Error loading notification sound:', e);
                // Report the detailed error info to console
                const errorInfo = {
                    code: audio.error?.code,
                    message: audio.error?.message,
                    src: audio.src,
                    readyState: audio.readyState
                };
                console.error('🔔 Audio error details:', errorInfo);
            };
            
            // Play the sound and handle any errors
            audio.play().catch(e => {
                console.error('🔔 Could not play notification sound:', e);
                
                // Common causes of audio playback failures:
                if (e.name === 'NotAllowedError') {
                    console.info('🔔 Audio playback was blocked because page hasn\'t been interacted with yet');
                } else if (e.name === 'AbortError') {
                    console.info('🔔 Audio playback was aborted');
                } else if (e.name === 'NotSupportedError') {
                    console.info('🔔 Audio format not supported by the browser');
                }
            });
        } catch (error) {
            console.error('🔔 Error setting up notification sound:', error);
        }
    }

    /**
     * Check if notification preferences allow a certain notification type
     * @param {string} type Notification type
     * @private
     */
    _checkNotificationPreferences(type) {
        // First check if browser notifications are enabled (master switch)
        if (!this.notificationPreferences.browser_notifications_enabled && 
            !this.notificationPreferences.browserNotificationsEnabled) {
            return false;
        }
        
        // Then check specific notification types
        switch (type) {
            case 'message':
                return this.notificationPreferences.message_notifications !== false && 
                       this.notificationPreferences.messageNotifications !== false;
            case 'contact':
                return this.notificationPreferences.contact_notifications !== false && 
                       this.notificationPreferences.contactNotifications !== false;
            default:
                return true;
        }
    }

    /**
     * Resolve contact name from message object
     * @param {Object} message Message object
     * @private
     */
    _resolveContactName(message) {
        // Try to extract a name from the client object if it exists
        let clientObjectName = '';
        if (message.client && typeof message.client === 'object') {
            clientObjectName = message.client.name || message.client.display_name || '';
        }

        // Try to find contact info in nested objects
        let contactInfo = {};
        if (message.client && typeof message.client === 'object' && message.client.contact) {
            contactInfo = message.client.contact;
        } else if (message.contact && typeof message.contact === 'object') {
            contactInfo = message.contact;
        }

        // Get contact details with fallbacks
        const contactName = message.sender_name ||
            message.client_name ||
            message.name ||
            message.contact_name ||
            clientObjectName ||
            (contactInfo.name || '') ||
            'Contact';

        // Get a better name from global contacts if available
        const clientId = message.client_id;
        let nameFromContacts = '';

        try {
            if (window.__BROADCASTER_CONTACTS__) {
                const contact = window.__BROADCASTER_CONTACTS__.find(c =>
                    parseInt(c.id) === parseInt(clientId));
                if (contact && contact.name) {
                    nameFromContacts = contact.name;
                }
            }
        } catch (err) {
            console.debug('Error trying to find contact in window objects:', err);
        }

        // Final resolved name
        return contactName !== 'Contact' ? contactName : nameFromContacts || `Contact ${clientId}`;
    }

    /**
     * Set up event listeners
     * @private
     */
    _setupEventListeners() {
        // Listen for company ID updates
        window.addEventListener('broadcaster-company-id', (event) => {
            if (event.detail && event.detail.companyId) {
                this.companyId = event.detail.companyId;
                console.debug('🔔 NotificationService updated companyId:', this.companyId);
            }
        });
        
        // Listen for DND settings changes from DndSettings component
        window.addEventListener('dnd-settings-changed', (event) => {
            if (event.detail) {
                console.debug('NotificationService: DND settings updated:', event.detail);
                this.notificationPreferences = {
                    ...this.notificationPreferences,
                    dnd: event.detail,
                };
            }
        });

        // Also listen for preference updates
        window.addEventListener('broadcaster-preferences-updated', (event) => {
            if (event.detail && event.detail.preferences) {
                console.debug('🔔 NotificationService updating preferences from event:', event.detail.preferences);
                this.notificationPreferences = {
                    ...this.notificationPreferences,
                    ...event.detail.preferences
                };
            }
        });
    }
    
    /**
     * Fetch user notification preferences from API
     * @private
     */
    _fetchUserPreferences() {
        // Skip if no fetch API or we're not in a browser
        if (typeof fetch === 'undefined' || typeof window === 'undefined') {
            return;
        }
        
        // Get CSRF token
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
        if (!csrfToken) {
            console.warn('🔔 NotificationService: CSRF token not found, skipping preferences fetch');
            return;
        }
        
        // Fetch preferences from API
        try {
            fetch('/api/user/notification-preferences', {
                headers: {
                    'Accept': 'application/json',
                    'X-CSRF-TOKEN': csrfToken
                }
            })
            .then(response => response.json())
            .then(data => {
                if (data.success && data.data) {
                    console.debug('🔔 NotificationService: Fetched user preferences:', data.data);
                    
                    // Update preferences
                    this.notificationPreferences = {
                        ...this.notificationPreferences,
                        ...data.data
                    };
                    
                    // Store globally for other components
                    window.__BROADCASTER_USER_PREFERENCES = data.data;
                    
                    // Emit event for other components
                    this._dispatchEvent('broadcaster-preferences-updated', {
                        preferences: data.data
                    });
                }
            })
            .catch(error => {
                console.error('🔔 NotificationService: Error fetching user preferences:', error);
            });
        } catch (error) {
            console.error('🔔 NotificationService: Exception in _fetchUserPreferences:', error);
        }
    }

    /**
     * Save notification preference to server
     * @param {string} key Preference key
     * @param {any} value Preference value
     * @private
     */
    _saveNotificationPreference(key, value) {
        // Only save if we have CSRF token
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
        if (!csrfToken) {
            console.warn('Cannot save notification preference: CSRF token not found');
            return;
        }

        // Save to server
        fetch('/api/user/notification-preferences', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': csrfToken
            },
            body: JSON.stringify({
                [key]: value
            })
        }).catch(error => {
            console.error('Error saving notification preference:', error);
        });
    }

    /**
     * Dispatch a custom event
     * @param {string} eventName Event name
     * @param {Object} data Event data
     * @private
     */
    _dispatchEvent(eventName, data) {
        window.dispatchEvent(new CustomEvent(eventName, {
            detail: data
        }));
    }
}

// Create singleton instance
const notificationService = new NotificationService();

// Export the singleton
export default notificationService;